type ReorderColumnsFromIdsParams<TColumn extends string> = {
  readonly nextOrderIds: readonly string[];
  readonly columnIdToColumn: Readonly<Partial<Record<string, TColumn>>>;
  readonly visibleColumns: readonly TColumn[];
};

export function dedupeItems<T>(items: readonly T[]) {
  const seen = new Set<T>();
  return items.filter((item) => {
    if (seen.has(item)) return false;
    seen.add(item);
    return true;
  });
}

/**
 * Keeps `pin` glued directly after `anchor` whatever order the stored selection, the picker, or a
 * drag produced. Computed relative to `anchor`'s current index rather than a fixed slot, so the
 * pinned column follows its anchor if the advisor moves that one.
 */
export function pinColumnAfter<T extends string>(
  columns: readonly T[],
  // NoInfer keeps the column union from widening to `string` when the caller passes literals,
  // which would erase the type of the returned order at every call site.
  { pin, anchor }: { pin: NoInfer<T>; anchor: NoInfer<T> },
): T[] {
  if (!columns.includes(pin)) return [...columns];

  const rest = columns.filter((column) => column !== pin);
  const anchorIndex = rest.indexOf(anchor);
  if (anchorIndex === -1) return [pin, ...rest];

  return [...rest.slice(0, anchorIndex + 1), pin, ...rest.slice(anchorIndex + 1)];
}

export function reorderColumnsFromIds<TColumn extends string>({
  nextOrderIds,
  columnIdToColumn,
  visibleColumns,
}: ReorderColumnsFromIdsParams<TColumn>) {
  const reorderedVisible = dedupeItems(nextOrderIds)
    .map((columnId) => columnIdToColumn[columnId])
    .filter((column): column is TColumn => column !== undefined);

  const nonRendered = visibleColumns.filter((column) => !reorderedVisible.includes(column));
  return dedupeItems([...reorderedVisible, ...nonRendered]);
}
